import { useSelector } from 'react-redux'
import { tagHotjarRecording } from '../helpers/util'
import { getShiptos } from '../repositories/CompanyRepository'
import { getAssetById } from '../repositories/FleetRepository'
import {
    createEquipmentRequest,
    createPartsRequest,
    createRentalRequest,
    createServiceRequest,
    getSingleRequest,
    getServiceEstimate,
    getUsedEquipmentDetails,
    listRepairRequestsForEquipment,
    listPMRequestsForEquipment,
    listEstimateRequestsForEquipment,
    missingEquipmentImageLog,
    requestUsedEquipment,
    searchUsedEquipment,
    estimateApproval,
    updatePONumber,
    getAllRequests as getAllRequestsFromRepository,
    getPMRequests as getPMRequestsFromRepository,
} from '../repositories/RequestRepository'
import { useRequestActions } from '../store/slices/RequestSlice'
import useGlobalLoading from './useGlobalLoading'

const useRequest = () => {
    const {
        saveRequestFilters,
        resetAllFilters,
        requestError,
        requestLoading,
        requestSuccess,
        clearAssetStore,
        storeAssetDetails,
        storeLocations,
        storeRequestList,
        storePMRequestList,
        saveFilters,
        savePmsFilters,
        savePastPmsFilters,
        saveEstimateFilters,
        savePastRequestsFilters,
        clearRequests,
        changeNeedsUpdate,
        changeNeedsUpdatePM,
    } = useRequestActions()
    const { changeLoading } = useGlobalLoading()

    const needsUpdateRequest = useSelector(
        state => state?.RequestSlice?.needsUpdateRequest,
    )
    const needsUpdateDashboard = useSelector(
        state => state?.RequestSlice?.needsUpdateDashboard,
    )
    const needsUpdatePM = useSelector(
        state => state?.RequestSlice?.needsUpdatePM,
    )
    const filters = useSelector(state => state?.RequestSlice?.filters)
    const pastRequestsFilters = useSelector(
        state => state?.RequestSlice?.pastRequestsFilters,
    )
    const pmsFilters = useSelector(state => state?.RequestSlice?.pmsFilters)
    const pastPmsFilters = useSelector(
        state => state?.RequestSlice?.pastPmsFilters,
    )
    const estimateFilters = useSelector(
        state => state?.RequestSlice?.estimateFilters,
    )
    const locations = useSelector(state => state?.RequestSlice?.locations)
    const openRequests = useSelector(state => state?.RequestSlice?.openRequests)
    const estimateRequests = useSelector(
        state => state?.RequestSlice?.estimateRequests,
    )
    const closedRequests = useSelector(
        state => state?.RequestSlice?.closedRequests,
    )
    const openPMRequests = useSelector(
        state => state?.RequestSlice?.openPMRequests,
    )
    const closedPMRequests = useSelector(
        state => state?.RequestSlice?.closedPMRequests,
    )
    const make = useSelector(state => state?.RequestSlice?.make)
    const model = useSelector(state => state?.RequestSlice?.model)
    const requestFilters = useSelector(
        state => state?.RequestSlice?.requestFilters,
    )
    const formIsLoading = useSelector(
        state => state?.RequestSlice?.formIsLoading,
    )
    const formError = useSelector(state => state?.RequestSlice?.formError)

    const loadLocations = (
        companyId,
        locPrefs,
        getServiceRequest = true,
        getPMRequest = true,
    ) => {
        changeLoading(true)

        getShiptos(companyId)
            .then(response => {
                let shiptos = response.data.shiptos
                storeLocations(shiptos)
                if (getServiceRequest) {
                    getAllRequests(locPrefs)
                }
                if (getPMRequest) getPMRequests(locPrefs)
            })
            .catch(() => {
                tagHotjarRecording('API Error')
                requestError('Unable to load customer locations.')
                changeLoading(false)
            })
    }

    const loadEquipment = payload => {
        requestLoading(true)
        const { equipmentId, companyId } = payload
        getAssetById({
            equipmentId,
            companyId,
        })
            .then(response => {
                let asset = response.data.asset

                storeAssetDetails(asset)
                requestLoading(false)
            })
            .catch(error => {
                tagHotjarRecording('API Error')
                let message = error.response.data.message
                requestError(message)
            })
    }

    const getAllRequests = locations => {
        changeLoading(true)
        getAllRequestsFromRepository(locations)
            .then(response => {
                changeLoading(false)
                let openRequests = response.data.openRequests
                let closedRequests = response.data.closedRequests
                let estimateRequests = response.data.estimateRequests
                storeRequestList(openRequests, closedRequests, estimateRequests)
            })
            .catch(() => {
                changeLoading(false)
                tagHotjarRecording('API Error')
                requestError('Unable to load requests.')
                requestLoading(false)
            })
    }

    const getPMRequests = locations => {
        changeLoading(true)
        getPMRequestsFromRepository(locations)
            .then(response => {
                changeLoading(false)
                let openPMRequests = response.data.openPMRequests
                let closedPMRequests = response.data.closedPMRequests
                storePMRequestList(openPMRequests, closedPMRequests)
            })
            .catch(() => {
                changeLoading(false)
                tagHotjarRecording('API Error')
                requestError('Unable to load PM requests.')
                requestLoading(false)
            })
    }

    const handleCreateEquipmentRequest = (data, onCreated, onError) => {
        return createEquipmentRequest(data)
            .then(onCreated)
            .catch(onError)
    }

    const handleListRepairRequestForEquipment = (
        equipmentId,
        companyId,
        onGetted,
        onError,
    ) => {
        return listRepairRequestsForEquipment(equipmentId, companyId)
            .then(onGetted)
            .catch(onError)
    }
    const handleListPMRequestForEquipment = (
        equipmentId,
        companyId,
        onGetted,
        onError,
    ) => {
        return listPMRequestsForEquipment(equipmentId, companyId)
            .then(onGetted)
            .catch(onError)
    }
    const handleListEstimateRequestForEquipment = (
        equipmentId,
        companyId,
        onGetted,
        onError,
    ) => {
        return listEstimateRequestsForEquipment(equipmentId, companyId)
            .then(onGetted)
            .catch(onError)
    }

    const handleCreateRequestUsedEquipment = (params, onCreated, onError) => {
        return requestUsedEquipment(params)
            .then(onCreated)
            .catch(onError)
    }

    const handleCreatePartsRequest = (params, onCreated, onError) => {
        return createPartsRequest(params)
            .then(onCreated)
            .catch(onError)
    }

    const handleCreateRentalRequest = (params, onCreated, onError) => {
        return createRentalRequest(params)
            .then(onCreated)
            .catch(onError)
    }

    const handleGetUsedEquipmentDetails = (
        equipamentId,
        requestUUID,
        onGetted,
        onError,
    ) => {
        return getUsedEquipmentDetails(equipamentId, requestUUID)
            .then(onGetted)
            .catch(onError)
    }

    const handleSearchUsedEquipment = (params, card, onSearched, onError) => {
        return searchUsedEquipment(params)
            .then(response => onSearched(response, card))
            .catch(onError)
    }

    const handleMissingEquipmentImageLog = data => {
        return missingEquipmentImageLog(data)
    }

    const handleCreateServiceRequest = (data, onCreated, onError) => {
        return createServiceRequest(data)
            .then(onCreated)
            .catch(onError)
    }

    const handleGetRequestInfo = (reqId, reqType, onGetted, onError) => {
        return getSingleRequest(reqId, reqType)
            .then(onGetted)
            .catch(onError)
    }

    const handleGetEstimateInfo = (estimateId, onGetted, onError) => {
        return getServiceEstimate(estimateId)
            .then(onGetted)
            .catch(onError)
    }

    const handleEstimateApproval = (params, onSuccess, onError) => {
        return estimateApproval(params)
            .then(onSuccess)
            .catch(onError)
    }

    const handleUpdatePO = (params, onSuccess, onError) => {
        return updatePONumber(params)
            .then(onSuccess)
            .catch(onError)
    }

    return {
        handleCreateEquipmentRequest,
        handleListRepairRequestForEquipment,
        handleListPMRequestForEquipment,
        handleListEstimateRequestForEquipment,
        handleCreateRequestUsedEquipment,
        handleCreatePartsRequest,
        handleCreateRentalRequest,
        handleGetUsedEquipmentDetails,
        handleSearchUsedEquipment,
        handleMissingEquipmentImageLog,
        handleCreateServiceRequest,
        handleGetRequestInfo,
        handleGetEstimateInfo,
        handleEstimateApproval,
        handleUpdatePO,
        saveRequestFilters,
        resetAllFilters,
        requestError,
        requestLoading,
        requestSuccess,
        clearAssetStore,
        storeAssetDetails,
        storeLocations,
        storeRequestList,
        storePMRequestList,
        loadLocations,
        loadEquipment,
        getAllRequests,
        getPMRequests,
        saveFilters,
        clearRequests,
        locations,
        openRequests: openRequests,
        closedRequests: closedRequests,
        openPMRequests: openPMRequests,
        closedPMRequests: closedPMRequests,
        estimateRequests: estimateRequests,
        make,
        model,
        requestFilters: {
            requestFilters,
            locationFilters: [requestFilters?.locationFilters],
            typeFilters: [requestFilters?.typeFilters],
            statusFilters: [requestFilters?.statusFilters],
        },
        formIsLoading,
        formError,
        filters,
        pastRequestsFilters,
        savePastRequestsFilters,
        savePmsFilters,
        savePastPmsFilters,
        saveEstimateFilters,
        pmsFilters,
        pastPmsFilters,
        estimateFilters,
        changeNeedsUpdate,
        changeNeedsUpdatePM,
        needsUpdateRequest,
        needsUpdateDashboard,
        needsUpdatePM,
    }
}

export default useRequest
